/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    queryOptions,
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {
    getAllArticles,
    getArticleById,
    getFeaturedArticles,
    getHomepageArticles,
} from './articles.api';

const DEFAULT_ARTICLES_PAGE_SIZE = 9;

// Article content is effectively static, so it never needs to be refetched
// within a session (avoids re-fetching on every hover/focus/remount).
const ARTICLES_STALE_TIME = Infinity;

export const ARTICLES_QUERY_KEYS = {
    homepage: ['articles', 'homepage'] as const,
    featured: ['articles', 'featured'] as const,
    all: (limit: number) => ['articles', 'all', limit] as const,
    byId: (articleId: string) => ['articles', 'byId', articleId] as const,
};

export function useHomepageArticlesQuery() {
    return useQuery({
        queryKey: ARTICLES_QUERY_KEYS.homepage,
        queryFn: async ({ signal }) => getHomepageArticles(signal),
        staleTime: ARTICLES_STALE_TIME,
        retry: 1,
    });
}

export function useFeaturedArticlesQuery() {
    return useQuery({
        queryKey: ARTICLES_QUERY_KEYS.featured,
        queryFn: async ({ signal }) => getFeaturedArticles(signal),
        staleTime: ARTICLES_STALE_TIME,
        retry: 1,
    });
}

function articleByIdOptions(articleId: string) {
    return queryOptions({
        queryKey: ARTICLES_QUERY_KEYS.byId(articleId),
        queryFn: async ({ signal }) => getArticleById(articleId, signal),
        enabled: !!articleId,
        staleTime: ARTICLES_STALE_TIME,
        retry: 1,
    });
}

export function useArticleByIdQuery(articleId: string) {
    return useQuery(articleByIdOptions(articleId));
}

/** Returns a function that warms the cache for a single article (e.g. on hover). */
export function usePrefetchArticleById() {
    const queryClient = useQueryClient();

    return (articleId: string) =>
        queryClient.prefetchQuery(articleByIdOptions(articleId));
}

export function useAllArticlesInfiniteQuery() {
    return useInfiniteQuery({
        queryKey: ARTICLES_QUERY_KEYS.all(DEFAULT_ARTICLES_PAGE_SIZE),
        queryFn: async ({ pageParam, signal }) =>
            getAllArticles(
                {
                    offset: pageParam * DEFAULT_ARTICLES_PAGE_SIZE,
                    limit: DEFAULT_ARTICLES_PAGE_SIZE,
                },
                signal
            ),
        initialPageParam: 0,
        staleTime: ARTICLES_STALE_TIME,
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.reduce(
                (total, page) => total + page.result.records.length,
                0
            );

            return loaded < lastPage.result.total_row ? allPages.length : undefined;
        },
    });
}
